import React, { useState } from 'react';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import AddDimensionModal from './AddDimensionModal';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';

const DimensionBuilder = ({
  quotaProjectId,
  dimensions,
  cells = [], // Existing cells to calculate allocated quotas
  overallTarget,
  pendingModifications = [], // Pending cell modifications (to use pending targets)
  isLocked = false,
  onDimensionAdded,
  onDimensionUpdated,
  onDimensionDeleted
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDimension, setEditingDimension] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDimensionId, setDeleteDimensionId] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  const handleDelete = (dimensionId) => {
    setDeleteDimensionId(dimensionId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/quotas/projects/${quotaProjectId}/dimensions/${deleteDimensionId}`);
      onDimensionDeleted(deleteDimensionId);
      setShowDeleteConfirm(false);
      setDeleteDimensionId(null);
    } catch (error) {
      console.error('Error deleting dimension:', error);
      setShowDeleteConfirm(false);
      setDeleteDimensionId(null);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors de la suppression',
        type: 'error'
      });
    }
  };

  return (
    <div className="dimension-builder">
      {!isLocked && (
        <div className="dimension-builder-header">
          <button 
            className="btn-primary btn-sm"
            onClick={() => setShowAddModal(true)}
            title="Ajouter une dimension"
          >
            <FontAwesomeIcon icon={faPlus} />
            Ajouter une dimension
          </button>
        </div>
      )}

      <div className="dimensions-list">
        {dimensions.length === 0 ? (
          <div className="empty-state">
            <p>Aucune dimension définie</p>
            <p className="hint">Cliquez sur "Ajouter une dimension" pour commencer</p>
          </div>
        ) : (
          dimensions.map((dimension, index) => (
            <div key={dimension.id} className="dimension-item">
              <div className="dimension-drag-handle">
                <FontAwesomeIcon icon={faGripVertical} />
              </div>
              <div className="dimension-content">
                <div className="dimension-header">
                  <h4>{dimension.label}</h4>
                  {!isLocked && (
                    <div className="dimension-actions">
                      <button
                        className="btn-icon"
                        onClick={() => setEditingDimension(dimension)}
                        title="Modifier"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDelete(dimension.id)}
                        title="Supprimer"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="dimension-info">
                  <span className="dimension-question">
                    Question: {dimension.limesurvey_question_code}
                  </span>
                  {dimension.subquestion_code && (
                    <span className="dimension-subquestion">
                      Sous-question: {dimension.subquestion_code}
                    </span>
                  )}
                  <span className="dimension-values-count">
                    {dimension.values_json?.length || 0} valeur(s)
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <AddDimensionModal
          quotaProjectId={quotaProjectId}
          cells={cells}
          overallTarget={overallTarget}
          existingDimensions={dimensions}
          pendingModifications={pendingModifications}
          onClose={() => setShowAddModal(false)}
          onSuccess={(newDimension) => {
            onDimensionAdded(newDimension);
            setShowAddModal(false);
          }}
        />
      )}

      {editingDimension && (
        <AddDimensionModal
          quotaProjectId={quotaProjectId}
          dimension={editingDimension}
          cells={cells}
          overallTarget={overallTarget}
          existingDimensions={dimensions}
          pendingModifications={pendingModifications}
          onClose={() => setEditingDimension(null)}
          onSuccess={(updatedDimension) => {
            onDimensionUpdated(updatedDimension);
            setEditingDimension(null);
          }}
        />
      )}

      <ConfirmationModal
        show={showDeleteConfirm}
        title="Supprimer la dimension"
        message="Êtes-vous sûr de vouloir supprimer cette dimension ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteDimensionId(null);
        }}
      />

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default DimensionBuilder;

